<template>
  <div class="communication-panel space-y-4">
    <!-- Alertes absences du jour -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800 flex items-center gap-2">📢 Communications du jour</h3>
        <button @click="showAddMessage = true" class="text-blue-600 text-sm font-medium">+ Nouveau</button>
      </div>

      <!-- Messages actifs -->
      <div class="space-y-2">
        <div v-for="msg in messages" :key="msg.id" :class="getMessageClass(msg.type)" class="rounded-lg p-3 border-l-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-semibold text-sm">{{ msg.title }}</span>
                <span class="text-xs bg-white px-2 py-1 rounded">{{ msg.source }}</span>
              </div>
              <p class="text-sm text-gray-700">{{ msg.content }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ msg.time }}</p>
            </div>
            <button
              v-if="msg.actionRequired"
              @click="acknowledgeMessage(msg.id)"
              class="ml-3 text-xs bg-white px-3 py-1 rounded-full font-medium hover:bg-gray-100"
            >
              ✓ Vu
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Absences déclarées -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800">👥 Absences déclarées</h3>
        <button
          @click="showAbsenceForm = true"
          class="text-sm bg-blue-500 text-white px-3 py-1 rounded-lg font-medium hover:bg-blue-600"
        >
          Déclarer
        </button>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="bg-red-50 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-red-600">{{ totalAbsences }}</div>
          <div class="text-xs text-gray-600">Absences aujourd'hui</div>
        </div>
        <div class="bg-green-50 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-green-600">{{ adjustedBarquettes }} barquettes</div>
          <div class="text-xs text-gray-600">Quantité ajustée</div>
        </div>
      </div>

      <!-- Détail par type -->
      <div v-if="absencesByType.length > 0" class="mt-3 space-y-2">
        <p class="text-xs font-semibold text-gray-600">Par type :</p>
        <div v-for="item in absencesByType" :key="item.type" class="flex justify-between text-sm">
          <span class="text-gray-700">{{ item.type }}</span>
          <span class="font-semibold text-red-600">-{{ item.absences }} élèves</span>
        </div>
      </div>
    </div>

    <!-- Spécificités alimentaires -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800">🍽️ Spécificités du jour</h3>
        <button @click="showSpecificitiesForm = true" class="text-blue-600 text-sm font-medium">Modifier</button>
      </div>

      <div class="space-y-2">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Régimes sans porc</span>
          <span class="font-semibold text-blue-600">{{ dietaryRestrictions.noPork }}</span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Allergies gluten</span>
          <span class="font-semibold text-orange-600">{{ dietaryRestrictions.glutenFree }}</span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Allergies lactose</span>
          <span class="font-semibold text-orange-600">{{ dietaryRestrictions.lactoseFree }}</span>
        </div>
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Végétariens</span>
          <span class="font-semibold text-green-600">{{ dietaryRestrictions.vegetarian }}</span>
        </div>
      </div>
    </div>

    <!-- Historique communication -->
    <button
      @click="showHistory = true"
      class="w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-3 text-sm font-medium text-gray-700 transition"
    >
      📋 Voir l'historique des communications
    </button>

    <!-- Modal ajout message -->
    <div v-if="showAddMessage" class="modal-overlay" @click="showAddMessage = false">
      <div class="modal-content" @click.stop>
        <h3 class="font-bold text-lg mb-4">Nouveau message</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-semibold mb-1">Type</label>
            <select v-model="newMessage.type" class="w-full p-2 border-2 rounded-lg">
              <option value="absence">Absence</option>
              <option value="info">Information</option>
              <option value="alert">Alerte</option>
              <option value="dietary">Régime alimentaire</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Titre</label>
            <input
              v-model="newMessage.title"
              class="w-full p-2 border-2 rounded-lg"
              placeholder="Ex: Sortie scolaire CM2"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Message</label>
            <textarea
              v-model="newMessage.content"
              class="w-full p-2 border-2 rounded-lg"
              rows="3"
              placeholder="Détails..."
            ></textarea>
          </div>
          <div class="flex gap-2">
            <button @click="addMessage" class="flex-1 bg-blue-500 text-white py-2 rounded-lg font-semibold">
              Envoyer
            </button>
            <button @click="showAddMessage = false" class="flex-1 bg-gray-200 py-2 rounded-lg font-semibold">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal absence -->
    <div v-if="showAbsenceForm" class="modal-overlay" @click="showAbsenceForm = false">
      <div class="modal-content" @click.stop>
        <h3 class="font-bold text-lg mb-4">Déclarer des absences</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-semibold mb-1">Type</label>
            <select v-model="newAbsence.type" class="w-full p-2 border-2 rounded-lg">
              <option value="PS">PS</option>
              <option value="MS">MS</option>
              <option value="GS">GS</option>
              <option value="CP">CP</option>
              <option value="CE1">CE1</option>
              <option value="CE2">CE2</option>
              <option value="CM1">CM1</option>
              <option value="CM2">CM2</option>
              <option value="ANIM-MAT">ANIM-MAT</option>
              <option value="ANIM-ELE">ANIM-ELE</option>
              <option value="ENSEIGNANT">ENSEIGNANT</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Nombre d'absents</label>
            <input v-model.number="newAbsence.count" type="number" min="1" class="w-full p-2 border-2 rounded-lg" />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Motif (optionnel)</label>
            <input
              v-model="newAbsence.reason"
              class="w-full p-2 border-2 rounded-lg"
              placeholder="Ex: Sortie scolaire"
            />
          </div>
          <div class="flex gap-2">
            <button @click="addAbsence" class="flex-1 bg-blue-500 text-white py-2 rounded-lg font-semibold">
              Valider
            </button>
            <button @click="showAbsenceForm = false" class="flex-1 bg-gray-200 py-2 rounded-lg font-semibold">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CommunicationPanel",
  data() {
    return {
      messages: [
        {
          id: 1,
          type: "absence",
          title: "Sortie scolaire CM2",
          content: "28 élèves absents - Visite au musée",
          source: "École",
          time: "08:45",
          actionRequired: true,
        },
        {
          id: 2,
          type: "dietary",
          title: "Nouveau régime",
          content: "2 nouveaux élèves végétariens en CE1",
          source: "Infirmerie",
          time: "09:12",
          actionRequired: false,
        },
        {
          id: 3,
          type: "info",
          title: "Menu modifié",
          content: "Remplacement du poisson par du poulet suite aux retours négatifs",
          source: "Cuisine centrale",
          time: "10:30",
          actionRequired: false,
        },
      ],
      totalAbsences: 28,
      computed: {
        adjustedBarquettes() {
          // Regroupe les absences par type (PS, MS, GS, CP, CE1, CE2, CM1, CM2, ANIM-MAT, ANIM-ELE, ENSEIGNANT)
          const abs = this.absencesByType || []
          const get = (types) => abs.filter((a) => types.includes(a.type)).reduce((sum, a) => sum + a.absences, 0)
          const petits = get(["PS", "MS", "GS"])
          const grands = get(["CP", "CE1", "CE2", "CM1", "CM2"])
          const anims = get(["ANIM-MAT", "ANIM-ELE", "ENSEIGNANT"])
          const val = petits / 6.5 + grands / 6 + anims / 5
          return val.toFixed(2)
        },
      },
      absencesByType: [{ type: "CM2", absences: 28 }],
      dietaryRestrictions: {
        noPork: 12,
        glutenFree: 3,
        lactoseFree: 5,
        vegetarian: 8,
      },
      showAddMessage: false,
      showAbsenceForm: false,
      showSpecificitiesForm: false,
      showHistory: false,
      newMessage: {
        type: "info",
        title: "",
        content: "",
      },
      newAbsence: {
        type: "CP",
        count: 1,
        reason: "",
      },
    }
  },
  computed: {
    adjustedBarquettes() {
      const abs = this.absencesByType || []
      const get = (types) => abs.filter((a) => types.includes(a.type)).reduce((sum, a) => sum + a.absences, 0)
      const petits = get(["PS", "MS", "GS"])
      const grands = get(["CP", "CE1", "CE2", "CM1", "CM2"])
      const anims = get(["ANIM-MAT", "ANIM-ELE", "ENSEIGNANT"])
      const val = petits / 6.5 + grands / 6 + anims / 5
      return val.toFixed(2)
    },
  },
  methods: {
    getMessageClass(type) {
      const classes = {
        absence: "bg-red-50 border-red-500",
        info: "bg-blue-50 border-blue-500",
        alert: "bg-orange-50 border-orange-500",
        dietary: "bg-green-50 border-green-500",
      }
      return classes[type] || "bg-gray-50 border-gray-500"
    },
    acknowledgeMessage(id) {
      const msg = this.messages.find((m) => m.id === id)
      if (msg) {
        msg.actionRequired = false
        alert("Message marqué comme lu")
      }
    },
    addMessage() {
      if (!this.newMessage.title || !this.newMessage.content) {
        alert("Veuillez remplir tous les champs")
        return
      }
      this.messages.unshift({
        id: Date.now(),
        ...this.newMessage,
        source: "Vous",
        time: new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
        actionRequired: false,
      })
      this.newMessage = { type: "info", title: "", content: "" }
      this.showAddMessage = false
      alert("Message envoyé à tous les utilisateurs")
    },
    addAbsence() {
      if (!this.newAbsence.type || this.newAbsence.count < 1) {
        alert("Veuillez remplir tous les champs")
        return
      }
      this.totalAbsences += this.newAbsence.count
      // Ajout ou maj du type dans absencesByType
      const existing = this.absencesByType.find((c) => c.type === this.newAbsence.type)
      if (existing) {
        existing.absences += this.newAbsence.count
      } else {
        this.absencesByType.push({
          type: this.newAbsence.type,
          absences: this.newAbsence.count,
        })
      }
      this.messages.unshift({
        id: Date.now(),
        type: "absence",
        title: `Absences ${this.newAbsence.type}`,
        content: `${this.newAbsence.count} élèves absents${
          this.newAbsence.reason ? " - " + this.newAbsence.reason : ""
        }`,
        source: "Vous",
        time: new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
        actionRequired: false,
      })
      this.newAbsence = { type: "CP", count: 1, reason: "" }
      this.showAbsenceForm = false
      alert("Absence déclarée et quantités ajustées automatiquement")
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.items-start {
  align-items: flex-start;
}
.items-center {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.border-l-4 {
  border-left-width: 4px;
}
.border-2 {
  border-width: 2px;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-green-50 {
  background-color: #f0fdf4;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-gray-50 {
  background-color: #f9fafb;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-200 {
  background-color: #e5e7eb;
}
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}
.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.bg-blue-600 {
  background-color: #2563eb;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.border-red-500 {
  border-color: #ef4444;
}
.border-blue-500 {
  border-color: #3b82f6;
}
.border-green-500 {
  border-color: #22c55e;
}
.border-orange-500 {
  border-color: #f97316;
}
.border-gray-500 {
  border-color: #6b7280;
}
.text-white {
  color: #ffffff;
}
.text-blue-600 {
  color: #2563eb;
}
.text-red-600 {
  color: #dc2626;
}
.text-green-600 {
  color: #16a34a;
}
.text-orange-600 {
  color: #ea580c;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.font-medium {
  font-weight: 500;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-center {
  text-align: center;
}
.block {
  display: block;
}
.p-1 {
  padding: 0.25rem;
}
.p-2 {
  padding: 0.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.px-2 {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mt-1 {
  margin-top: 0.25rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.ml-3 {
  margin-left: 0.75rem;
}
.w-full {
  width: 100%;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:hover {
  opacity: 0.9;
}
input,
select,
textarea {
  font-family: inherit;
  border-color: #e5e7eb;
}
input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 90%;
  width: 400px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
}
</style>
